/**
 * Conditional Console Logger
 * Overrides console.log, console.info, console.debug, and console.group methods
 * to only show when debug mode is enabled
 * Enable via: ?devconsoleoutput=1 URL parameter or automatically on *.myshopify.com domains
 * Uses localStorage to persist debug state across page navigations and redirects
 *
 * Usage:
 * - Add ?devconsoleoutput=1 to any URL to enable debug logs (persists via localStorage)
 * - Add ?devconsoleoutput=0 or ?devconsoleoutput=false to disable debug logs (removes localStorage flag)
 * - Call window.enableDebug() in browser console to enable
 * - Call window.disableDebug() in browser console to disable
 *
 * Note: console.error and console.warn always remain active for critical issues
 */
(function() {
  const STORAGE_KEY = 'consolelogsenabled';

  // Save original console methods
  const originalConsole = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    debug: console.debug.bind(console),
    group: console.group.bind(console),
    groupCollapsed: console.groupCollapsed.bind(console),
    groupEnd: console.groupEnd.bind(console)
  };

  // Check URL parameters and update localStorage accordingly
  function handleDebugParameter() {
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('devconsoleoutput');

    if (debugParam === '1') {
      // Enable debug mode
      try {
        localStorage.setItem(STORAGE_KEY, 'true');
      } catch (e) {
        originalConsole.warn('Could not set localStorage for debug mode:', e);
      }
    } else if (debugParam === '0' || debugParam === 'false') {
      // Disable debug mode
      try {
        localStorage.removeItem(STORAGE_KEY);
        originalConsole.log('%c[Console Output Disabled]', 'color: #dc3545; font-weight: bold;');
      } catch (e) {
        originalConsole.warn('Could not remove localStorage for debug mode:', e);
      }
    }
  }

  // Check if debug mode is enabled
  function isDebugEnabled() {
    // Check localStorage flag
    try {
      if (localStorage.getItem(STORAGE_KEY) === 'true') {
        return true;
      }
    } catch (e) {
      // localStorage might be disabled
    }

    // Auto-enable on myshopify.com domains (dev/preview themes)
    if (window.location.hostname.includes('myshopify.com')) {
      return true;
    }

    return false;
  }

  // Handle debug parameter on page load
  handleDebugParameter();

  // Override console.log to respect debug flag
  console.log = function(...args) {
    if (isDebugEnabled()) {
      originalConsole.log(...args);
    }
  };

  // Override console.info to respect debug flag
  console.info = function(...args) {
    if (isDebugEnabled()) {
      originalConsole.info(...args);
    }
  };

  // Override console.debug to respect debug flag
  console.debug = function(...args) {
    if (isDebugEnabled()) {
      originalConsole.debug(...args);
    }
  };

  // Override console.group to respect debug flag
  console.group = function(...args) {
    if (isDebugEnabled()) {
      originalConsole.group(...args);
    }
  };

  // Override console.groupCollapsed to respect debug flag
  console.groupCollapsed = function(...args) {
    if (isDebugEnabled()) {
      originalConsole.groupCollapsed(...args);
    }
  };

  // Override console.groupEnd to respect debug flag
  console.groupEnd = function(...args) {
    if (isDebugEnabled()) {
      originalConsole.groupEnd(...args);
    }
  };

  // Helper function to enable debug mode (sets localStorage)
  window.enableDebug = function() {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
      originalConsole.log('%c[Console Output Enabled]', 'color: #42a962; font-weight: bold;');
      originalConsole.log('Console output will now appear. Reload the page to see the changes.');
    } catch (e) {
      originalConsole.error('Could not enable debug mode:', e);
    }
  };

  // Helper function to disable debug mode (removes localStorage)
  window.disableDebug = function() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      originalConsole.log('%c[Console Output Disabled]', 'color: #dc3545; font-weight: bold;');
      originalConsole.log('Console output is now suppressed. Reload the page to see the changes.');
    } catch (e) {
      originalConsole.error('Could not disable debug mode:', e);
    }
  };

  // Note: console.error and console.warn are left untouched and always active

  // Log initial state if debug is enabled
  if (isDebugEnabled()) {
    originalConsole.log('%c[Console Output Enabled]', 'color: #42a962; font-weight: bold;');
  }
})();
